import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiChevronDown, FiSettings, FiLogOut, FiMenu, FiX } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../assets/logos/Unique_physique-Web2.png';
import './Navigation.css';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userProfile, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        {/* Logo */}
        <Link to="/" className="nav-logo-link">
          <img src={logo} alt="Unique Physique" className="nav-logo" />
        </Link>

        {/* Hamburger Menu Button */}
        <button
          className="hamburger-menu"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>

        {/* Navigation Items */}
        <div className={`nav-items ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <Link
            to="/about"
            className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`}
          >
            About
          </Link>
          <Link
            to="/shop"
            className={`nav-link ${location.pathname === '/shop' ? 'active' : ''}`}
          >
            Shop
          </Link>
          <Link
            to="/blog"
            className={`nav-link ${location.pathname === '/blog' ? 'active' : ''}`}
          >
            Blog
          </Link>
          <Link
            to="/contact"
            className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}
          >
            Contact
          </Link>

          {/* Auth Section */}
          {currentUser && userProfile ? (
            <div className="user-menu">
              <button
                className="user-menu-trigger"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <div className="user-avatar">
                  {userProfile.photoURL ? (
                    <img src={userProfile.photoURL} alt={userProfile.displayName} />
                  ) : (
                    <div className="avatar-placeholder">
                      {userProfile.displayName?.charAt(0) || 'U'}
                    </div>
                  )}
                </div>
                <div className="user-info">
                  <span className="user-name">{userProfile.displayName}</span>
                  <span className="user-type">
                    {userProfile.role === 'trainer' ? 'Coach' : 'Client'}
                  </span>
                </div>
                <FiChevronDown className={`dropdown-icon ${dropdownOpen ? 'open' : ''}`} />
              </button>

              {dropdownOpen && (
                <div className="user-dropdown">
                  <Link
                    to={userProfile.role === 'trainer' ? '/unique-physique/trainer-dashboard' : '/unique-physique/client-dashboard'}
                    className="dropdown-item"
                    onClick={() => setDropdownOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to={userProfile.role === 'trainer' ? '/unique-physique/trainer-dashboard/settings' : '/unique-physique/client-dashboard/settings'}
                    className="dropdown-item"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <FiSettings size={16} />
                    Settings
                  </Link>
                  <button className="dropdown-item logout" onClick={handleLogout}>
                    <FiLogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/signup" className="nav-link signup">
                Sign Up
              </Link>
              <Link to="/login" className="nav-link login">
                Login
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
